import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Waves, 
  Flame, 
  Snowflake, 
  Wind, 
  Utensils,
  Trash2,
  ArrowRight
} from 'lucide-react';

const services = [
  {
    id: 'washer',
    name: 'Washer',
    description: 'Top-load, front-load, and combo units',
    icon: Waves,
  },
  {
    id: 'dryer',
    name: 'Dryer',
    description: 'Gas, electric, and ventless models',
    icon: Wind,
  },
  {
    id: 'refrigerator',
    name: 'Refrigerator',
    description: 'All types including freezers',
    icon: Snowflake,
  },
  {
    id: 'oven',
    name: 'Oven, Range & Stove',
    description: 'Gas, electric, and induction',
    icon: Flame,
  },
  {
    id: 'dishwasher',
    name: 'Dishwasher',
    description: 'Built-in and portable units',
    icon: Utensils,
  },
  {
    id: 'disposal',
    name: 'Garbage Disposal',
    description: 'All makes and models',
    icon: Trash2,
  },
];

const ServicesSection = () => {
  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            Our Services
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Choose Your Appliance Type
          </h2>
          <p className="text-lg text-muted-foreground">
            Our professional technicians are trained to repair all major appliance brands and types.
            Select your appliance below to get started.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <Link
              key={service.id}
              to={`/book?service=${service.id}`}
              data-testid={`service-card-${service.id}`}
              className="service-card group relative overflow-hidden rounded-2xl bg-white border border-border p-8 hover:border-primary/50 transition-all duration-300"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="space-y-4">
                <div className="appliance-icon">
                  <service.icon className="w-8 h-8 text-primary transition-colors duration-300" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-foreground group-hover:text-primary transition-colors">
                  {service.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {service.description}
                </p>
                <div className="flex items-center text-primary font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  Book Repair <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            </Link>
          ))}

          {/* CTA Card */}
          <div 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-8 text-white"
            data-testid="service-cta-card"
          >
            <div className="space-y-4">
              <h3 className="font-heading font-semibold text-xl">
                Not Sure What's Wrong?
              </h3>
              <p className="text-white/80 text-sm">
                Our technicians can diagnose any appliance issue. Book a diagnostic visit today.
              </p>
              <Link to="/book">
                <Button
                  data-testid="service-cta-btn"
                  className="bg-white text-primary hover:bg-white/90 font-semibold rounded-full"
                >
                  Schedule Diagnostic
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
